import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Crown, Shield, Sparkles, BookOpen, Palette, Puzzle, Gamepad2, BookMarked, Truck, Tag, Gift, Award, Notebook } from "lucide-react";
import { useMembership } from "../lib/MembershipContext";

const ROYALTY_BENEFITS = [
  { Icon: BookOpen, label: "Unlimited Stories" },
  { Icon: Palette, label: "Unlimited Coloring Pages" },
  { Icon: Puzzle, label: "Unlimited Puzzles" },
  { Icon: Gamepad2, label: "Unlimited Games" },
  { Icon: BookMarked, label: "Complete Kingdom Dictionary" },
  { Icon: Truck, label: "Free Shipping (where applicable)" },
  { Icon: Tag, label: "Book & Store Discounts" },
  { Icon: Gift, label: "Exclusive Seasonal Content" },
  { Icon: Award, label: "Premium Certificates" },
  { Icon: Notebook, label: "Printable Kingdom Yearbooks" },
];

const ADVENTURER_BENEFITS = [
  { Icon: BookOpen, label: "More Stories, Coloring & Puzzles" },
  { Icon: Notebook, label: "Kingdom Journal & Feelings Check-In" },
  { Icon: Sparkles, label: "Child Profiles & Parent Dashboard" },
  { Icon: Award, label: "Badges, Streaks & Certificates" },
  { Icon: Gift, label: "Birthday Recognition & Showcase" },
];

// Global Kingdom upgrade modal — reads the pending prompt from MembershipContext.
export default function UpgradeModal() {
  const { upgrade, closeUpgrade, access } = useMembership();
  const nav = useNavigate();
  if (!upgrade) return null;

  const goGuest = upgrade.requiredLevel === "adventurer" || access.isGuest;
  const toMembership = () => { closeUpgrade(); nav("/membership"); };
  const toSignup = () => { closeUpgrade(); nav("/kingdom/login"); };

  const benefits = goGuest ? ADVENTURER_BENEFITS : ROYALTY_BENEFITS;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="upgrade-modal">
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={closeUpgrade} />
      <div className="relative bg-cream rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="relative bg-gradient-to-br from-purpleK to-[#5a2c8f] px-7 py-6 text-white">
          <button onClick={closeUpgrade} data-testid="upgrade-close" className="absolute top-4 right-4 grid place-items-center h-9 w-9 rounded-full bg-white/15 hover:bg-white/25"><X size={20} /></button>
          <div className="text-3xl">{goGuest ? "🏰" : "👑"}</div>
          <h2 className="font-display font-extrabold text-2xl mt-2">
            {goGuest ? "Your adventure is just beginning!" : "You've discovered a Royal Adventure!"}
          </h2>
          <p className="text-white/85 text-sm mt-1.5">
            {goGuest
              ? "Become a Kingdom Adventurer (free!) to unlock more stories, coloring, puzzles, the Kingdom Journal, Feelings Check-In, birthday celebrations and much more."
              : "Upgrade to Kingdom Royalty to unlock the complete Kingdom experience — the full content library, exclusive resources, discounts, free shipping and more."}
          </p>
        </div>

        <div className="p-7">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-5">
            {benefits.map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-sm text-ink/80">
                <span className="grid place-items-center h-7 w-7 rounded-lg bg-purpleK/10 text-purpleK shrink-0"><b.Icon size={15} /></span>
                {b.label}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between rounded-xl bg-white px-4 py-2.5 mb-4 text-sm">
            <span className="text-ink/50">Current</span>
            <span className="font-display font-bold text-ink flex items-center gap-1">
              {access.levelEmoji} {access.levelLabel}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            {goGuest ? (
              <>
                <button onClick={toSignup} data-testid="upgrade-primary" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-purpleK text-white font-display font-extrabold py-3.5"><Shield size={18} /> Become a Kingdom Adventurer (Free)</button>
                <button onClick={toMembership} data-testid="upgrade-secondary" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-ink font-display font-bold py-3"><Crown size={16} className="text-goldDark" /> Explore Kingdom Royalty</button>
              </>
            ) : (
              <>
                <button onClick={toMembership} data-testid="upgrade-primary" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-purpleK text-white font-display font-extrabold py-3.5"><Crown size={18} /> Become Kingdom Royalty</button>
                <button onClick={toMembership} data-testid="upgrade-secondary" className="w-full rounded-full bg-white text-ink font-display font-bold py-3">📖 Compare Memberships</button>
              </>
            )}
            <button onClick={closeUpgrade} data-testid="upgrade-keep-exploring" className="w-full text-ink/50 text-sm py-1 mt-1">Keep exploring free content</button>
          </div>
        </div>
      </div>
    </div>
  );
}
